import { IconColor } from "@/components/ui/icon-container";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/utils/utils";
import { Close, Search } from "@dreamhub/designsystem/icons";
import { IconButton, IconButtonSize } from "@/components/ui/icon-button";

interface Props {
  inputValue?: string;
  setInputValue: (term: string) => void;
  placeholder: string;
  size?: "small" | "large";
  searchIconColor?: IconColor;
  tooltipText?: string;
  iconButtonSize?: IconButtonSize;
}

const SearchButtonInput = ({
  inputValue = "",
  setInputValue,
  placeholder,
  size = "large",
  searchIconColor = "primary",
  tooltipText,
  iconButtonSize = "md"
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(!!inputValue);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    if (inputValue) {
      setIsExpanded(true);
    }
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target) &&
        !inputValue
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputValue]);

  const handleButtonClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleClearInput = () => {
    setInputValue("");
    inputRef.current?.focus();
  };

  const SearchButton = () => (
    <IconButton
      data-test="search-button"
      variant="iconNoBg"
      size={iconButtonSize}
      iconColor={isExpanded ? "tertiary" : searchIconColor}
      onClick={handleButtonClick}
      tooltipText={!isExpanded && tooltipText ? tooltipText : undefined}
      className={cn(isExpanded && "hover:!bg-transparent", "shrink-0")}
    >
      <Search />
    </IconButton>
  );

  return (
    <div ref={containerRef} className="flex items-center">
      <div
        className={cn(
          "flex h-8 max-h-9 rounded-lg items-center overflow-hidden transition-all duration-300 ease-in-out",
          isExpanded
            ? "border border-primary bg-surface-focus-selected"
            : "",
          isExpanded && size === "small" && "w-56",
          isExpanded && size === "large" && "w-64",
          !isExpanded && "w-fit"
        )}
      >
        <SearchButton />

        <input
          data-test="search-input"
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={cn(
            "text-base font-normal leading-[18px] text-primary placeholder:text-tertiary transition-all duration-300 ease-in-out bg-transparent outline-none",
            isExpanded ? "w-full opacity-100 px-1 py-1" : "w-0 opacity-0 p-0"
          )}
        />
        {isExpanded && inputValue && (
          <IconButton
            data-test="clear-search"
            variant="iconNoBg"
            className="mr-1 shrink-0"
            size="xs"
            onClick={handleClearInput}
          >
            <Close />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default SearchButtonInput;
